import { useMemo, useRef } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import MarketExpansionService from "@app/overviews/market-expansion-dashboard/marketExpansionService";
import { MarketplaceMarketShareFilterCriteria } from "@app/overviews/market-expansion-dashboard/shared/MarketplaceMarketShareFilterCriteria";
import { MarketplacePayerPlanCount } from "@app/overviews/market-expansion-dashboard/shared/MarketplaceCountyPlanCounts";
import { MedicaidMarketShareFilterCriteria } from "@app/overviews/market-expansion-dashboard/shared/MedicaidMarketShareFilterCriteria";
import { MedicaidPayerPlanCount } from "@app/overviews/market-expansion-dashboard/shared/MedicaidCountyPrograms";
import { MARKET_EXPANSION_QUERY_KEYS } from "@app/overviews/market-expansion-dashboard/hooks/marketExpansionQueryKeys";

export function useGetMarketplacePayerPlanCounts(
  clientId: number | undefined,
  userId: number | undefined,
  filterCriteria: MarketplaceMarketShareFilterCriteria | undefined,
): UseQueryResult<MarketplacePayerPlanCount[]> {
  const service = useRef(new MarketExpansionService()).current;

  return useQuery<MarketplacePayerPlanCount[]>({
    enabled: !!filterCriteria,
    // Keeps the previous service area's payers on screen while a newly-selected one within the same client/user/state is fetching
    placeholderData: (previousData, previousQuery) => {
      if (!filterCriteria || !previousQuery) return undefined;
      const [, previousClientId, previousUserId, previousStateCode] = previousQuery.queryKey;
      return previousClientId === clientId && previousUserId === userId && previousStateCode === filterCriteria.stateCode
        ? previousData
        : undefined;
    },
    queryFn: ({ signal }) => service.searchMarketplacePayerDistinctPlanCounts(filterCriteria!, signal),
    queryKey: [MARKET_EXPANSION_QUERY_KEYS.marketplacePayerPlanCounts, clientId, userId, filterCriteria?.stateCode, filterCriteria?.countySSAs],
    staleTime: 5 * 60 * 1000,
  });
}

export function useGetMedicaidPayerPlanCounts(
  clientId: number | undefined,
  userId: number | undefined,
  filterCriteria: MedicaidMarketShareFilterCriteria | undefined,
): UseQueryResult<MedicaidPayerPlanCount[]> {
  const service = useMemo(() => new MarketExpansionService(), []);

  return useQuery<MedicaidPayerPlanCount[]>({
    enabled: !!filterCriteria,
    // Keeps the previous service area's payers on screen while a newly-selected one within the same client/user/state is fetching
    placeholderData: (previousData, previousQuery) => {
      if (!filterCriteria || !previousQuery) return undefined;
      const [, previousClientId, previousUserId, previousStateCode] = previousQuery.queryKey;
      return previousClientId === clientId && previousUserId === userId && previousStateCode === filterCriteria.stateCode
        ? previousData
        : undefined;
    },
    queryFn: ({ signal }) => service.searchMedicaidPayerDistinctPlanCounts(filterCriteria!, signal),
    queryKey: [MARKET_EXPANSION_QUERY_KEYS.medicaidPayerPlanCounts, clientId, userId, filterCriteria?.stateCode, filterCriteria?.countySSAs],
    staleTime: 5 * 60 * 1000,
  });
}
